import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import api from "../services/api";
import "./ViewPayroll.css";
import { toast } from "react-toastify";
import { FaTrash, FaChartLine } from "react-icons/fa";
import { FaFilePdf } from "react-icons/fa6";
import {
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend
} from "recharts";
import RunNewPay from "./RunNewPay";
import EditLastPayroll from "./EditLastPayroll";
import PayList from "./PayList";


/* ======================================================
   SVG TAB DEFS + COMPONENT (rendered once per page)
   ====================================================== */
function TabSvgDefs() {
  return (
    <svg width="0" height="0" style={{ position: "absolute", inset: 0, pointerEvents: "none" }}>
      <defs>
        <filter id="tabShadow">
          <feComponentTransfer in="SourceGraphic">
            <feFuncR type="discrete" tableValues="0" />
            <feFuncG type="discrete" tableValues="0" />
            <feFuncB type="discrete" tableValues="0" />
          </feComponentTransfer>
          <feGaussianBlur stdDeviation="1" />
          <feComponentTransfer>
            <feFuncA type="linear" slope="0.2" />
          </feComponentTransfer>
          <feOffset dx="5" dy="1" result="shadow" />
          <feComposite in="SourceGraphic" />
        </filter>

        {/* Inactive gradient (blue-grey like the mock) */}
        <linearGradient id="tab-inactive-bg" x1="0%" y1="0%" x2="0%" y2="65%">
          <stop offset="0%" stopColor="#E5ECF6" />
          <stop offset="100%" stopColor="#D7E0ED" />
        </linearGradient>

        {/* Active gradient (white) */}
        <linearGradient id="tab-active-bg" x1="0%" y1="0%" x2="0%" y2="65%">
          <stop offset="0%" stopColor="#FFFFFF" />
          <stop offset="100%" stopColor="#FAFBFC" />
        </linearGradient>

        {/* Tab shape with left inward curve + right downward slope */}
        <symbol id="tab-shape" viewBox="0 0 122 40">
          <path d="M116.486,29.036c-23.582-8-14.821-29-42.018-29h-62.4C5.441,0.036,0,5.376,0,12.003v28.033h122v-11H116.486z" />
        </symbol>
      </defs>
    </svg>
  );
}

function SvgTabs({ tabs, activeIndex, onChange, desktopOnlyIndexSet = new Set() }) {
  return (
    <nav className="svg-tabs" role="navigation" aria-label="Payroll sections">
      <ul className="svg-tablist">
        {tabs.map((label, i) => {
          const active = i === activeIndex;
          return (
            <li
              key={label}
              className={`svg-tab ${active ? "is-active" : ""} ${desktopOnlyIndexSet.has(i) ? "svg-tab--desktop-only" : ""}`}
            >
              <button
                type="button"
                className="svg-tab-link"
                onClick={() => onChange(i)}
                aria-selected={active}
                aria-controls={`panel-${i}`}
                role="tab"
              >
                <span className="svg-tab-label">{label}</span>
                <svg className="svg-tab-bg" viewBox="0 0 122 40" aria-hidden="true">
                  <use xlinkHref="#tab-shape" />
                </svg>
              </button>
            </li>
          );
        })}
      </ul>
    </nav>
  );
}

/* ======================================================
   PAGE
   ====================================================== */
export default function ViewPayrollPage() {
  const { id: businessId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  /** -------- Tabs (string <-> index mapping) -------- */
  const TAB_KEYS = ["overview", "new", "all", "editlast"];
  const TAB_LABELS = ["Overview", "New Payroll", "Reports", "Edit Last Payroll"];
  const qsTab = new URLSearchParams(location.search).get("tab");
  const initialTab = TAB_KEYS.includes(qsTab) ? qsTab : "overview";
  const [activeTab, setActiveTab] = useState(initialTab);
  const activeTabIndex = Math.max(0, TAB_KEYS.indexOf(activeTab));
  const setActiveByIndex = (i) => {
    const next = TAB_KEYS[i] ?? "overview";
    setActiveTab(next);
    const sp = new URLSearchParams(location.search);
    sp.set("tab", next);
    navigate({ pathname: location.pathname, search: `?${sp.toString()}` }, { replace: true });
  };
  const desktopOnlyTabs = new Set([TAB_KEYS.indexOf("editlast")]);

  /** -------- Periods & Records -------- */
  const [payPeriods, setPayPeriods] = useState([]);
  const [expandedPeriods, setExpandedPeriods] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  /** -------- Selection -------- */
  const [selectedRecords, setSelectedRecords] = useState({});
  const [selectAll, setSelectAll] = useState({});

  /** -------- Stats & Income -------- */
  const [statsByPayDate, setStatsByPayDate] = useState({});
  const [incomeByPeriod, setIncomeByPeriod] = useState({});
  
  const [showChartsModal, setShowChartsModal] = useState(false);

 
  /** =================== Fetchers =================== */
  const fetchPayPeriods = useCallback(async () => {
    try {
      const { data } = await api.get(`/payroll/periods/${businessId}`);
      if (!data?.length) {
        navigate(`/dashboard/business/${businessId}/start-payroll`);
        // keep user on page; they can run "New Payroll" from the tab
      } else {
        setPayPeriods(data);
      }
      setError(null);
    } catch (err) {
      console.error("Error fetching pay periods:", err);
      setError("Failed to fetch pay periods. Please try again.");
    } finally {
      setLoading(false);
    }
  }, [businessId, navigate]);

  useEffect(() => { fetchPayPeriods(); }, [fetchPayPeriods]);


  useEffect(() => {
    const urlTab = new URLSearchParams(location.search).get("tab");
    if (urlTab && TAB_KEYS.includes(urlTab) && urlTab !== activeTab) {
      setActiveTab(urlTab);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search]);


  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const res = await api.get(`/payroll/stats/${businessId}`);
        const rows = Array.isArray(res.data) ? res.data : [];
        const map = {};
        for (const r of rows) map[dateKey(r.pay_date)] = r;
        if (mounted) setStatsByPayDate(map);
      } catch (e) {
        console.error("Error fetching payroll stats:", e);
      }
    })();
    return () => { mounted = false; };
    
  }, [businessId, payPeriods.length]);

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const { data } = await api.get(`/business/${businessId}/income-totals/all-periods`);
        const map = {};
        for (const r of (data?.periods || [])) {
          const k = `${dateKey(r.start_date)}_${dateKey(r.end_date)}_${dateKey(r.pay_date)}`;
          map[k] = Number(r.total_income || 0);
        }
        if (mounted) setIncomeByPeriod(map);
      } catch (e) {
        console.error("Error fetching all-period income totals:", e);
      }
    })();
    return () => { mounted = false; };
  }, [businessId, payPeriods.length]);

  /** =================== Helpers =================== */
  const toNum = (v) => {
    if (v == null) return null;
    if (typeof v === "number") return Number.isFinite(v) ? v : null;
    const n = parseFloat(String(v).replace(/[^0-9.-]/g, ""));
    return Number.isFinite(n) ? n : null;
  };

  const fmtCurrency = (n) =>
    typeof n === "number"
      ? n.toLocaleString(undefined, { style: "currency", currency: "USD" })
      : "-";

  const fmtNumber = (n, digits = 2) =>
    typeof n === "number"
      ? n.toLocaleString(undefined, { minimumFractionDigits: digits, maximumFractionDigits: digits })
      : "-";

  const showNum = (v) => (v == null || Number(v) === 0 ? "-" : fmtNumber(v));
  const fmtPercent = (n, digits = 1) =>
    typeof n === "number" && Number.isFinite(n) ? `${n.toFixed(digits)}%` : "-";

  function dateKey(d) {
    if (!d) return "";
    if (typeof d === "string") return d.slice(0, 10);
    return new Date(d).toISOString().slice(0, 10);
  }

  const periodKeyOf = (p) =>
    `${dateKey(p.start_date)}_${dateKey(p.end_date)}_${dateKey(p.pay_date)}`;

  const fmtShortDate = (s) => {
    if (!s) return "";
    const d = new Date(s);
    return d.toLocaleDateString();
  };

  const fmtPeriodLabel = (p) => {
  const s = new Date(p.start_date);
  const e = new Date(p.end_date);

  const format = (d) =>
    d.toLocaleString(undefined, {
      month: "short",
      day: "2-digit",
    });

  return `${format(s)} - ${format(e)}, ${e.getFullYear()}`;
};

  const fmtCurrencyAxis = (v) =>
    v == null
      ? ""
      : new Intl.NumberFormat(undefined, {
          style: "currency",
          currency: "USD",
          notation: "compact",
          maximumFractionDigits: 1,
        }).format(Number(v));

  const fmtNumberAxis = (v) =>
    v == null ? "" : Number(v).toLocaleString(undefined, { maximumFractionDigits: 2 });

  const calcDomain = (data, key) => {
    const vals = data.map(d => d[key]).filter(Number.isFinite);
    if (!vals.length) return [0, 1];
    const min = Math.min(...vals), max = Math.max(...vals);
    if (min === max) return min === 0 ? [0, 1] : [min * 0.95, max * 1.05];
    return [Math.floor(min), Math.ceil(max)];
  };

  /** -------- Chart data + Month selector -------- */
  const chartData = useMemo(() => {
    const rows = Object.entries(statsByPayDate).map(([date, r]) => ({
      date,
      payroll_cost: toNum(r?.payroll_cost),
      total_hours: toNum(r?.total_hours),
      overtime_hours: toNum(r?.overtime_hours),
      avg_pay_rate: toNum(r?.avg_pay_rate),
    }));
    rows.sort((a, b) => a.date.localeCompare(b.date));
    return rows;
  }, [statsByPayDate]);

  const months = useMemo(() => {
    const set = new Set(chartData.map(d => d.date.slice(0, 7)));
    return Array.from(set).sort();
  }, [chartData]);

  const monthLabel = (ym) => {
    if (!ym) return "";
    const [y, m] = ym.split("-");
    const d = new Date(Number(y), Number(m) - 1, 1);
    return `${d.toLocaleString(undefined, { month: "short" })}-${String(d.getFullYear()).slice(-2)}`;
  };

  const monthOptions = useMemo(
    () => months.map(ym => ({ value: ym, label: monthLabel(ym) })),
    [months]
  );

  const latestMonth = months[months.length - 1] || "";
  const [selectedMonth, setSelectedMonth] = useState(latestMonth);
  useEffect(() => { setSelectedMonth(latestMonth); }, [latestMonth]);

  const lastThreeMonths = useMemo(() => {
  if (months.length === 0) return [];
  const last = months.slice(-3); // last 3 entries (already sorted)
  return last;
}, [months]);

const monthFiltered = useMemo(() => {
  return chartData.filter(d => lastThreeMonths.includes(d.date.slice(0, 7)));
}, [chartData, lastThreeMonths]);


  /** =================== Selection =================== */
  const handleCheckboxChange = (periodKey, record) => {
    setSelectedRecords((prev) => {
      const updated = { ...prev };
      if (!updated[periodKey]) updated[periodKey] = [];
      const exists = updated[periodKey].find((r) => r.id === record.id);
      updated[periodKey] = exists
        ? updated[periodKey].filter((r) => r.id !== record.id)
        : [...updated[periodKey], record];

      setSelectAll((prevAll) => ({
        ...prevAll,
        [periodKey]: updated[periodKey].length === (expandedPeriods[periodKey]?.length || 0),
      }));
      return updated;
    });
  };

  const handleSelectAll = (periodKey, records) => {
    setSelectAll((prev) => {
      const isSelectingAll = !prev[periodKey];
      setSelectedRecords((prevRecords) => ({
        ...prevRecords,
        [periodKey]: isSelectingAll ? records : [],
      }));
      return { ...prev, [periodKey]: isSelectingAll };
    });
  };

  /** =================== Actions =================== */
  const handlePrint = async () => {
    try {
      const allSelectedRecords = Object.values(selectedRecords).flat();
      const response = await api.post(
        `/payroll/print`,
        { ids: allSelectedRecords.map((r) => r.id) },
        { responseType: "blob" }
      );
      const blob = new Blob([response.data], { type: "application/pdf" });
      const link = document.createElement("a");
      link.href = URL.createObjectURL(blob);
      link.download = "payroll_records.pdf";
      link.click();
      setSelectedRecords({});
      setSelectAll({});
    } catch (err) {
      console.error("Error printing payroll records:", err);
      alert("Failed to print selected records. Please try again.");
    }
  };

  const handleDelete = async () => {
    try {
      const allSelected = Object.values(selectedRecords).flat();
      if (!allSelected.length) {
        toast.error("No records selected for deletion.");
        return;
      }
      await api.post(`/payroll/delete`, { payrollData: allSelected });
      await fetchPayPeriods();

      setExpandedPeriods((prev) => {
        const updated = { ...prev };
        Object.keys(updated).forEach((k) => {
          if (updated[k]) {
            updated[k] = updated[k].filter(
              (r) => !allSelected.some((sel) => sel.id === r.id)
            );
          }
        });
        return updated;
      });

      setSelectedRecords({});
      setSelectAll({});
      toast.success("Selected records deleted successfully!");
    } catch (err) {
      console.error("Error deleting payroll records:", err);
      toast.error("Failed to delete selected records. Please try again.");
    }
  };

  const toggleExpandPeriod = async (period) => {
    const key = periodKeyOf(period);
    if (expandedPeriods[key]) {
      setExpandedPeriods((prev) => ({ ...prev, [key]: null }));
    } else {
      try {
        const { data } = await api.get(`/payroll/records/${businessId}`, {
          params: {
            start_date: dateKey(period.start_date),
            end_date: dateKey(period.end_date),
            pay_date: dateKey(period.pay_date),
          },
        });
        setExpandedPeriods((prev) => ({ ...prev, [key]: data }));
      } catch (err) {
        console.error("Error fetching payroll records:", err);
        alert("Failed to fetch payroll records. Please try again.");
      }
    }
  };

  /** =================== Render =================== */
  if (loading) return <p>Loading payroll periods...</p>;
  if (error) return <p>{error}</p>;

  return (
    <div className="view-payroll-page">
      <TabSvgDefs />

<div className="title-row">
      <h1 className="pw-page-title">Payroll</h1>
      </div>
      <div className="vp-header-row">
        {monthFiltered.length > 0 && (
          <button
            type="button"
            className="vp-graph-button"
            onClick={() => setShowChartsModal(true)}
          >
            <FaChartLine className="vp-graph-icon" />
          </button>
        )}
      </div>
      {/* ===== Charts ===== */}
      

      {/* ===== Charts Modal ===== */}
      {showChartsModal && (
        <div
          className="vp-modal-backdrop"
          onClick={() => setShowChartsModal(false)}
        >
          <div
            className="vp-modal"
            onClick={(e) => e.stopPropagation()} // prevent closing when clicking inside box
          >
            <div className="vp-modal-header">
              <h2>Payroll Trends</h2>
              <button
                type="button"
                className="vp-modal-close"
                onClick={() => setShowChartsModal(false)}
              >
                ×
              </button>
            </div>

            <div className="vp-modal-body">
              {monthFiltered.length > 0 ? (
                <div className="vp-charts-row">
                  {/* Cost & Rate */}
                  <div className="vp-chart-card">
                    <div className="vp-chart-head">
                      <div className="vp-chart-title">Cost &amp; Rate</div>
                      {monthOptions.length > 0 && (
                        <select
                          className="vp-month"
                          value={selectedMonth}
                          onChange={(e) => setSelectedMonth(e.target.value)}
                        >
                          {monthOptions.map((o) => (
                            <option key={o.value} value={o.value}>
                              {o.label}
                            </option>
                          ))}
                        </select>
                      )}
                    </div>
                    <ResponsiveContainer width="100%" height={240}>
                      <LineChart
                        data={monthFiltered}
                        margin={{ top: 8, right: 8, bottom: 0, left: 0 }}
                      >
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="date" tickFormatter={fmtShortDate} />
                        <YAxis
                          yAxisId="left"
                          width={70}
                          tickFormatter={fmtCurrencyAxis}
                          domain={calcDomain(monthFiltered, "payroll_cost")}
                        />
                        <YAxis
                          yAxisId="right"
                          orientation="right"
                          width={70}
                          tickFormatter={fmtCurrencyAxis}
                          domain={calcDomain(monthFiltered, "avg_pay_rate")}
                        />
                        <Tooltip
                          labelFormatter={fmtShortDate}
                          formatter={(v, name) => [
                            fmtCurrency(Number(v)),
                            name,
                          ]}
                        />
                        <Legend />
                        <Line
                          type="monotone"
                          yAxisId="left"
                          dataKey="payroll_cost"
                          name="Total Cost"
                          dot={false}
                          stroke="#f59e0b"
                          strokeWidth={3}
                        />
                        <Line
                          type="monotone"
                          yAxisId="right"
                          dataKey="avg_pay_rate"
                          name="Average Rate"
                          dot={false}
                          stroke="#ec4899"
                          strokeWidth={3}
                        />
                      </LineChart>
                    </ResponsiveContainer>
                  </div>

                  {/* Hours & Overtime */}
                  <div className="vp-chart-card">
                    <div className="vp-chart-head">
                      <div className="vp-chart-title">Hours &amp; Overtime</div>
                      {monthOptions.length > 0 && (
                        <select
                          className="vp-month"
                          value={selectedMonth}
                          onChange={(e) => setSelectedMonth(e.target.value)}
                        >
                          {monthOptions.map((o) => (
                            <option key={o.value} value={o.value}>
                              {o.label}
                            </option>
                          ))}
                        </select>
                      )}
                    </div>
                    <ResponsiveContainer width="100%" height={240}>
                      <LineChart
                        data={monthFiltered}
                        margin={{ top: 8, right: 8, bottom: 0, left: 0 }}
                      >
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="date" tickFormatter={fmtShortDate} />
                        <YAxis
                          width={60}
                          tickFormatter={fmtNumberAxis}
                          domain={calcDomain(monthFiltered, "total_hours")}
                        />
                        <Tooltip
                          labelFormatter={fmtShortDate}
                          formatter={(v, name) => [
                            Number(v).toLocaleString(undefined, {
                              maximumFractionDigits: 2,
                            }),
                            name,
                          ]}
                        />
                        <Legend />
                        <Line
                          type="monotone"
                          dataKey="total_hours"
                          name="Total Hours"
                          dot={false}
                          stroke="#22c55e"
                          strokeWidth={3}
                        />
                        <Line
                          type="monotone"
                          dataKey="overtime_hours"
                          name="Overtime Hours"
                          dot={false}
                          stroke="#3b82f6"
                          strokeWidth={3}
                        />
                      </LineChart>
                    </ResponsiveContainer>
                  </div>
                </div>
              ) : (
                <p>No stats available yet.</p>
              )}
            </div>
          </div>
        </div>
      )}



      <SvgTabs
      tabs={TAB_LABELS}
      activeIndex={activeTabIndex}
      desktopOnlyIndexSet={desktopOnlyTabs}
      onChange={setActiveByIndex}
      />
    

      {/* ===== Panels under tabs ===== */}
      <div className="svg-panels">
        {/* -------- Overview -------- */}
        <div id="panel-0" className={`pw-card tab-panel ${activeTab === "overview" ? "is-active" : ""}`}>
  <div className="pw-table-shell">
    <div className="pw-table-head">
      <table className="payroll-table-view payroll-table-view--fixed">
        <thead>
          <tr>
            <th className="t-left">Payroll Period</th>
            <th>Total Cost</th>
            <th>Average Rate</th>
            <th>Total Hours</th>
            <th>Overtime Hours</th>
            <th className="hide-header">Employees Paid</th>
           
          </tr>
        </thead>
      </table>
    </div>

    <div className="pw-table-body-scroll">
      <table className="payroll-table-view payroll-table-view--body">
        <tbody>
          {payPeriods.map((period) => {
            const key = periodKeyOf(period);
            const stats = statsByPayDate[dateKey(period.pay_date)];
            const avgRate = stats?.avg_pay_rate != null ? Number(stats.avg_pay_rate) : null;
            const totalCost = stats?.payroll_cost != null ? Number(stats.payroll_cost) : null;
            const totalHours = stats?.total_hours != null ? Number(stats.total_hours) : null;
            const otHours = stats?.overtime_hours != null ? Number(stats.overtime_hours) : null;
            const employeesPaid = Number.isFinite(Number(stats?.employees_paid))
              ? Number(stats.employees_paid)
              : "-";

            return (
              <React.Fragment key={key}>
                <tr onClick={() => toggleExpandPeriod(period)} className="clickable-row">
                  <td className="t-left">{fmtPeriodLabel(period)}</td>
                  <td className="payroll-amt">
                    <div>{fmtCurrency(totalCost)}</div>
                    <div className="subtext-percent">
                      {(() => {
                        const income = Number(incomeByPeriod[key] || 0);
                        if (!Number.isFinite(totalCost)) return "-";
                        if (income <= 0) return "0.0%";
                        return fmtPercent((totalCost / income) * 100, 1);
                      })()}
                    </div>
                  </td>
                  <td>{avgRate != null ? fmtCurrency(avgRate) : "-"}</td>
                  <td>{showNum(totalHours)}</td>
                  <td>{showNum(otHours)}</td>
                  <td className="hide-header">{employeesPaid}</td>
                  
                </tr>

                {expandedPeriods[key] && (
                  <tr>
                    <td colSpan="7">
                      {expandedPeriods[key].length > 0 ? (
                        <>
                          <div className="view-page-icons">
                            {Object.values(selectedRecords).flat().length > 0 && (
                              <FaFilePdf className="print-icon-page" onClick={handlePrint} title="Print Selected" />
                            )}
                            {Object.values(selectedRecords).flat().length > 0 && (
                              <FaTrash className="delete-icon-page" onClick={handleDelete} title="Delete Selected" />
                            )}
                          </div>

                          <table className="sub-payroll">
                            <thead>
                              <tr>
                                <th className="check-sel-head">
                                  <input
                                    className="check-input"
                                    type="checkbox"
                                    checked={selectAll[key] || false}
                                    onChange={() => handleSelectAll(key, expandedPeriods[key])}
                                  />
                                </th>
                                <th>Name</th>
                                <th>Gross Pay</th>
                                <th>Net Pay</th>
                                <th>Total Hours</th>
                              </tr>
                            </thead>
                            <tbody>
                              {expandedPeriods[key].map((record) => (
                                <tr key={record.id} className="sub-table-datarow">
                                  <td>
                                    <input
                                      className="single-check"
                                      type="checkbox"
                                      checked={selectedRecords[key]?.some((r) => r.id === record.id) || false}
                                      onChange={() =>
                                        handleCheckboxChange(key, {
                                          id: record.id,
                                          employee_id: record.employee_id,
                                          start_date: record.start_date,
                                          end_date: record.end_date,
                                          pay_date: record.pay_date,
                                        })
                                      }
                                    />
                                  </td>
                                  <td>{record.name || ""}</td>
                                  <td>{record.grosspay}</td>
                                  <td>{record.net_pay}</td>
                                  <td>
                                    {(
                                      Number(record.regularhours || 0) +
                                      Number(record.overtimehours || 0) +
                                      Number(record.doubletimehours || 0)
                                    ).toFixed(2)}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </>
                      ) : (
                        <p>No payroll records available for this period.</p>
                      )}
                    </td>
                  </tr>
                )}
              </React.Fragment>
            );
          })}
        </tbody>
      </table>
    </div>
  </div>
</div>

        {/* -------- New Payroll (embedded) -------- */}
        <div id="panel-1" className={`pw-card tab-panel ${activeTab === "new" ? "is-active" : ""}`}>
          {activeTab === "new" && (
            <RunNewPay
              embedded
              onDone={() => {
                fetchPayPeriods();
                setActiveTab("overview");
              }}
            />
          )}
        </div>

        {/* -------- All Payrolls -------- */}
        {/* -------- All Payrolls -------- */}
{/* -------- All Payrolls -------- */}
<div id="panel-2" className={`pw-card tab-panel ${activeTab === "all" ? "is-active" : ""}`}>
  {activeTab === "all" && <PayList embedded />}
</div>


        {/* <div id="panel-2" className={`pw-card tab-panel ${activeTab === "all" ? "is-active" : ""}`}>
          <div className="starteditbuttons">
            <button
              className="edit-last"
              onClick={() => navigate(`/dashboard/business/${businessId}/list-page`)}
            >
              Payroll List
            </button>
          </div>
        </div> */}

        {/* -------- Edit Last Payroll -------- */}
      <div id="panel-3" className={`pw-card tab-panel ${activeTab === "editlast" ? "is-active" : ""}`}>
      {activeTab === "editlast" && <EditLastPayroll embedded />}
      </div>
      </div>
    </div>
  );
}




// import React, { useCallback, useEffect, useMemo, useState } from "react";
// import { useNavigate, useParams } from "react-router-dom";
// import api from "../services/api";
// import "./ViewPayroll.css";
// import { toast } from "react-toastify";
// import { FaTrash, FaEllipsisV } from "react-icons/fa";
// import { FaFilePdf } from "react-icons/fa6";
// import {
//   ResponsiveContainer,
//   LineChart,
//   Line,
//   XAxis,
//   YAxis,
//   CartesianGrid,
//   Tooltip,
//   Legend
// } from "recharts";
// import RunNewPay from "./RunNewPay"; // adjust path if different


// export default function ViewPayrollPage() {
//   const { id: businessId } = useParams();
//   const navigate = useNavigate();

//   /** ---------------- Tabs ---------------- */
//   const [activeTab, setActiveTab] = useState("overview"); // "overview" | "new" | "all"

//   /** ---------------- Periods & Records ---------------- */
//   const [payPeriods, setPayPeriods] = useState([]);
//   const [expandedPeriods, setExpandedPeriods] = useState({});
//   const [loading, setLoading] = useState(true);
//   const [error, setError] = useState(null);

//   /** ---------------- Selection ---------------- */
//   const [selectedRecords, setSelectedRecords] = useState({});
//   const [selectAll, setSelectAll] = useState({});

//   /** ---------------- Stats & Income ---------------- */
//   // keyed by pay_date (YYYY-MM-DD)
//   const [statsByPayDate, setStatsByPayDate] = useState({});
//   // keyed by "start_end_pay" with normalized YYYY-MM-DDs
//   const [incomeByPeriod, setIncomeByPeriod] = useState({});

//   /** =================== Fetchers =================== */
//   const fetchPayPeriods = useCallback(async () => {
//     try {
//       const { data } = await api.get(`/payroll/periods/${businessId}`);
//       if (!data?.length) {
//         navigate(`/dashboard/business/${businessId}/start-payroll`);
//       } else {
//         setPayPeriods(data);
//       }
//       setError(null);
//     } catch (err) {
//       console.error("Error fetching pay periods:", err);
//       setError("Failed to fetch pay periods. Please try again.");
//     } finally {
//       setLoading(false);
//     }
//   }, [businessId, navigate]);

//   useEffect(() => {
//     fetchPayPeriods();
//   }, [fetchPayPeriods]);

//   useEffect(() => {
//     let mounted = true;
//     (async () => {
//       try {
//         const res = await api.get(`/payroll/stats/${businessId}`);
//         const rows = Array.isArray(res.data) ? res.data : [];
//         const map = {};
//         for (const r of rows) map[dateKey(r.pay_date)] = r;
//         if (mounted) setStatsByPayDate(map);
//       } catch (e) {
//         console.error("Error fetching payroll stats:", e);
//       }
//     })();
//     return () => { mounted = false; };
//   }, [businessId]);

//   useEffect(() => {
//     let mounted = true;
//     (async () => {
//       try {
//         const { data } = await api.get(`/business/${businessId}/income-totals/all-periods`);
//         const map = {};
//         for (const r of (data?.periods || [])) {
//           const k = `${dateKey(r.start_date)}_${dateKey(r.end_date)}_${dateKey(r.pay_date)}`;
//           map[k] = Number(r.total_income || 0);
//         }
//         if (mounted) setIncomeByPeriod(map);
//       } catch (e) {
//         console.error("Error fetching all-period income totals:", e);
//       }
//     })();
//     return () => { mounted = false; };
//   }, [businessId]);

//   /** =================== Helpers =================== */
//   const toNum = (v) => {
//     if (v == null) return null;
//     if (typeof v === "number") return Number.isFinite(v) ? v : null;
//     const n = parseFloat(String(v).replace(/[^0-9.-]/g, ""));
//     return Number.isFinite(n) ? n : null;
//   };

//   const fmtCurrency = (n) =>
//     typeof n === "number"
//       ? n.toLocaleString(undefined, { style: "currency", currency: "USD" })
//       : "-";

//   const fmtNumber = (n, digits = 2) =>
//     typeof n === "number"
//       ? n.toLocaleString(undefined, { minimumFractionDigits: digits, maximumFractionDigits: digits })
//       : "-";

//   const showNum = (v) => (v == null || Number(v) === 0 ? "-" : fmtNumber(v));
//   const fmtPercent = (n, digits = 1) =>
//     typeof n === "number" && Number.isFinite(n) ? `${n.toFixed(digits)}%` : "-";

//   function dateKey(d) {
//     if (!d) return "";
//     if (typeof d === "string") return d.slice(0, 10);
//     return new Date(d).toISOString().slice(0, 10);
//   }

//   const periodKeyOf = (p) =>
//     `${dateKey(p.start_date)}_${dateKey(p.end_date)}_${dateKey(p.pay_date)}`;

//   const fmtShortDate = (s) => {
//     if (!s) return "";
//     const d = new Date(s);
//     return d.toLocaleDateString();
//   };

//   const fmtPeriodLabel = (p) => {
//     const s = new Date(p.start_date);
//     const e = new Date(p.end_date);
//     const dd = (n) => String(n).padStart(2, "0");
//     const mon = e.toLocaleString(undefined, { month: "short" });
//     return `${dd(s.getDate())}-${dd(e.getDate())} ${mon} ${e.getFullYear()}`;
//   };

//   const fmtCurrencyAxis = (v) =>
//     v == null
//       ? ""
//       : new Intl.NumberFormat(undefined, {
//           style: "currency",
//           currency: "USD",
//           notation: "compact",
//           maximumFractionDigits: 1,
//         }).format(Number(v));

//   const fmtNumberAxis = (v) =>
//     v == null ? "" : Number(v).toLocaleString(undefined, { maximumFractionDigits: 2 });

//   const calcDomain = (data, key) => {
//     const vals = data.map(d => d[key]).filter(Number.isFinite);
//     if (!vals.length) return [0, 1];
//     const min = Math.min(...vals), max = Math.max(...vals);
//     if (min === max) return min === 0 ? [0, 1] : [min * 0.95, max * 1.05];
//     return [Math.floor(min), Math.ceil(max)];
//   };

//   /** -------- Chart data + Month selector -------- */
//   const chartData = useMemo(() => {
//     const rows = Object.entries(statsByPayDate).map(([date, r]) => ({
//       date,
//       payroll_cost: toNum(r?.payroll_cost),
//       total_hours: toNum(r?.total_hours),
//       overtime_hours: toNum(r?.overtime_hours),
//       avg_pay_rate: toNum(r?.avg_pay_rate),
//     }));
//     rows.sort((a, b) => a.date.localeCompare(b.date));
//     return rows;
//   }, [statsByPayDate]);

//   const months = useMemo(() => {
//     const set = new Set(chartData.map(d => d.date.slice(0, 7))); // YYYY-MM
//     return Array.from(set).sort();
//   }, [chartData]);

//   function monthLabel(ym) {
//     if (!ym) return "";
//     const [y, m] = ym.split("-");
//     const d = new Date(Number(y), Number(m) - 1, 1);
//     return `${d.toLocaleString(undefined, { month: "short" })}-${String(d.getFullYear()).slice(-2)}`;
//   }

//   const monthOptions = useMemo(
//     () => months.map(ym => ({ value: ym, label: monthLabel(ym) })),
//     [months]
//   );

//   const latestMonth = months[months.length - 1] || "";
//   const [selectedMonth, setSelectedMonth] = useState(latestMonth);
//   useEffect(() => { setSelectedMonth(latestMonth); }, [latestMonth]);

//   const monthFiltered = useMemo(
//     () => chartData.filter(d => !selectedMonth || d.date.slice(0, 7) === selectedMonth),
//     [chartData, selectedMonth]
//   );

//   /** =================== Selection =================== */
//   const handleCheckboxChange = (periodKey, record) => {
//     setSelectedRecords((prev) => {
//       const updated = { ...prev };
//       if (!updated[periodKey]) updated[periodKey] = [];
//       const exists = updated[periodKey].find((r) => r.id === record.id);
//       updated[periodKey] = exists
//         ? updated[periodKey].filter((r) => r.id !== record.id)
//         : [...updated[periodKey], record];

//       setSelectAll((prevAll) => ({
//         ...prevAll,
//         [periodKey]: updated[periodKey].length === (expandedPeriods[periodKey]?.length || 0),
//       }));
//       return updated;
//     });
//   };

//   const handleSelectAll = (periodKey, records) => {
//     setSelectAll((prev) => {
//       const isSelectingAll = !prev[periodKey];
//       setSelectedRecords((prevRecords) => ({
//         ...prevRecords,
//         [periodKey]: isSelectingAll ? records : [],
//       }));
//       return { ...prev, [periodKey]: isSelectingAll };
//     });
//   };

//   /** =================== Actions =================== */
//   const handlePrint = async () => {
//     try {
//       const allSelectedRecords = Object.values(selectedRecords).flat();
//       const response = await api.post(
//         `/payroll/print`,
//         { ids: allSelectedRecords.map((r) => r.id) },
//         { responseType: "blob" }
//       );
//       const blob = new Blob([response.data], { type: "application/pdf" });
//       const link = document.createElement("a");
//       link.href = URL.createObjectURL(blob);
//       link.download = "payroll_records.pdf";
//       link.click();
//       setSelectedRecords({});
//       setSelectAll({});
//     } catch (err) {
//       console.error("Error printing payroll records:", err);
//       alert("Failed to print selected records. Please try again.");
//     }
//   };

//   const handleDelete = async () => {
//     try {
//       const allSelected = Object.values(selectedRecords).flat();
//       if (!allSelected.length) {
//         toast.error("No records selected for deletion.");
//         return;
//       }
//       await api.post(`/payroll/delete`, { payrollData: allSelected });
//       await fetchPayPeriods();

//       setExpandedPeriods((prev) => {
//         const updated = { ...prev };
//         Object.keys(updated).forEach((k) => {
//           if (updated[k]) {
//             updated[k] = updated[k].filter(
//               (r) => !allSelected.some((sel) => sel.id === r.id)
//             );
//           }
//         });
//         return updated;
//       });

//       setSelectedRecords({});
//       setSelectAll({});
//       toast.success("Selected records deleted successfully!");
//     } catch (err) {
//       console.error("Error deleting payroll records:", err);
//       toast.error("Failed to delete selected records. Please try again.");
//     }
//   };

//   const toggleExpandPeriod = async (period) => {
//     const key = periodKeyOf(period);
//     if (expandedPeriods[key]) {
//       setExpandedPeriods((prev) => ({ ...prev, [key]: null }));
//     } else {
//       try {
//         const { data } = await api.get(`/payroll/records/${businessId}`, {
//           params: {
//             start_date: dateKey(period.start_date),
//             end_date: dateKey(period.end_date),
//             pay_date: dateKey(period.pay_date),
//           },
//         });
//         setExpandedPeriods((prev) => ({ ...prev, [key]: data }));
//       } catch (err) {
//         console.error("Error fetching payroll records:", err);
//         alert("Failed to fetch payroll records. Please try again.");
//       }
//     }
//   };

//   /** =================== Render =================== */
//   if (loading) return <p>Loading payroll periods...</p>;
//   if (error) return <p>{error}</p>;

//   return (
//     <div className="view-payroll-page">
//       <h1 className="vp-title">Payroll</h1>

//       {/* Charts row */}
//       {monthFiltered.length > 0 && (
//         <div className="vp-charts-row">
//           {/* Cost & Rate */}
//           <div className="vp-chart-card">
//             <div className="vp-chart-head">
//               <div className="vp-chart-title">Cost &amp; Rate</div>
//               {monthOptions.length > 0 && (
//                 <select
//                   className="vp-month"
//                   value={selectedMonth}
//                   onChange={(e) => setSelectedMonth(e.target.value)}
//                 >
//                   {monthOptions.map((o) => (
//                     <option key={o.value} value={o.value}>{o.label}</option>
//                   ))}
//                 </select>
//               )}
//             </div>
//             <ResponsiveContainer width="100%" height={240}>
//               <LineChart data={monthFiltered} margin={{ top: 8, right: 8, bottom: 0, left: 0 }}>
//                 <CartesianGrid strokeDasharray="3 3" />
//                 <XAxis dataKey="date" tickFormatter={fmtShortDate} />
//                 <YAxis
//                   yAxisId="left"
//                   width={70}
//                   tickFormatter={fmtCurrencyAxis}
//                   domain={calcDomain(monthFiltered, "payroll_cost")}
//                 />
//                 <YAxis
//                   yAxisId="right"
//                   orientation="right"
//                   width={70}
//                   tickFormatter={fmtCurrencyAxis}
//                   domain={calcDomain(monthFiltered, "avg_pay_rate")}
//                 />
//                 <Tooltip
//                   labelFormatter={fmtShortDate}
//                   formatter={(v, name) => [fmtCurrency(Number(v)), name]}
//                 />
//                 <Legend />
//                 <Line
//                   type="monotone"
//                   yAxisId="left"
//                   dataKey="payroll_cost"
//                   name="Total Cost"
//                   dot={false}
//                   stroke="#f59e0b" /* amber */
//                   strokeWidth={3}
//                 />
//                 <Line
//                   type="monotone"
//                   yAxisId="right"
//                   dataKey="avg_pay_rate"
//                   name="Average Rate"
//                   dot={false}
//                   stroke="#ec4899" /* pink */
//                   strokeWidth={3}
//                 />
//               </LineChart>
//             </ResponsiveContainer>
//           </div>

//           {/* Hours & Overtime */}
//           <div className="vp-chart-card">
//             <div className="vp-chart-head">
//               <div className="vp-chart-title">Hours &amp; Overtime</div>
//               {monthOptions.length > 0 && (
//                 <select
//                   className="vp-month"
//                   value={selectedMonth}
//                   onChange={(e) => setSelectedMonth(e.target.value)}
//                 >
//                   {monthOptions.map((o) => (
//                     <option key={o.value} value={o.value}>{o.label}</option>
//                   ))}
//                 </select>
//               )}
//             </div>
//             <ResponsiveContainer width="100%" height={240}>
//               <LineChart data={monthFiltered} margin={{ top: 8, right: 8, bottom: 0, left: 0 }}>
//                 <CartesianGrid strokeDasharray="3 3" />
//                 <XAxis dataKey="date" tickFormatter={fmtShortDate} />
//                 <YAxis
//                   width={60}
//                   tickFormatter={fmtNumberAxis}
//                   domain={calcDomain(monthFiltered, "total_hours")}
//                 />
//                 <Tooltip
//                   labelFormatter={fmtShortDate}
//                   formatter={(v, name) => [
//                     Number(v).toLocaleString(undefined, { maximumFractionDigits: 2 }),
//                     name
//                   ]}
//                 />
//                 <Legend />
//                 <Line
//                   type="monotone"
//                   dataKey="total_hours"
//                   name="Total Hours"
//                   dot={false}
//                   stroke="#22c55e" /* green */
//                   strokeWidth={3}
//                 />
//                 <Line
//                   type="monotone"
//                   dataKey="overtime_hours"
//                   name="Overtime Hours"
//                   dot={false}
//                   stroke="#3b82f6" /* blue */
//                   strokeWidth={3}
//                 />
//               </LineChart>
//             </ResponsiveContainer>
//           </div>
//         </div>
//       )}

//       {/* Curved tabs */}
//       <div className="pw-tabs vp-tabs">
//         <button
//           className={`pw-tab ${activeTab === "overview" ? "pw-tab-active" : ""}`}
//           onClick={() => setActiveTab("overview")}
//         >
//           Overview
//         </button>
//         <button
//           className={`pw-tab ${activeTab === "new" ? "pw-tab-active" : ""}`}
//           onClick={() => setActiveTab("new")}
//         >
//           New Payroll
//         </button>
//         <button
//           className={`pw-tab ${activeTab === "all" ? "pw-tab-active" : ""}`}
//           onClick={() => setActiveTab("all")}
//         >
//           All Payrolls
//         </button>
//       </div>

//       {/* -------- Overview panel -------- */}
//       <div className={`pw-card tab-panel ${activeTab === "overview" ? "is-active" : ""}`}>
//         <table className="payroll-table-view">
//           <thead>
//             <tr>
//               <th className="t-left">Payroll Period</th>
//               <th>Total Cost</th>
//               <th>Average Rate</th>
//               <th>Total Hours</th>
//               <th>Overtime Hours</th>
//               <th>Employees Paid</th>
//               <th className="t-right"></th>
//             </tr>
//           </thead>
//           <tbody>
//             {payPeriods.map((period) => {
//               const key = periodKeyOf(period);
//               const stats = statsByPayDate[dateKey(period.pay_date)];
//               const avgRate = stats?.avg_pay_rate != null ? Number(stats.avg_pay_rate) : null;
//               const totalCost = stats?.payroll_cost != null ? Number(stats.payroll_cost) : null;
//               const totalHours = stats?.total_hours != null ? Number(stats.total_hours) : null;
//               const otHours = stats?.overtime_hours != null ? Number(stats.overtime_hours) : null;
//               const employeesPaid =
//                 stats && Number.isFinite(Number(stats.employees_paid))
//                   ? Number(stats.employees_paid)
//                   : "-";

//               return (
//                 <React.Fragment key={key}>
//                   <tr onClick={() => toggleExpandPeriod(period)} className="clickable-row">
//                     <td className="t-left">{fmtPeriodLabel(period)}</td>
//                     <td>
//                       <div>{fmtCurrency(totalCost)}</div>
//                       <div className="subtext-percent">
//                         {(() => {
//                           const income = Number(incomeByPeriod[key] || 0);
//                           if (!Number.isFinite(totalCost)) return "-";
//                           if (income <= 0) return "0.0%";
//                           return fmtPercent((totalCost / income) * 100, 1);
//                         })()}
//                       </div>
//                     </td>
//                     <td>{avgRate != null ? fmtCurrency(avgRate) : "-"}</td>
//                     <td>{showNum(totalHours)}</td>
//                     <td>{showNum(otHours)}</td>
//                     <td>{employeesPaid}</td>
//                     <td className="t-right"><FaEllipsisV /></td>
//                   </tr>

//                   {expandedPeriods[key] && (
//                     <tr>
//                       <td colSpan="7">
//                         {expandedPeriods[key].length > 0 ? (
//                           <>
//                             <div className="view-page-icons">
//                               {Object.values(selectedRecords).flat().length > 0 && (
//                                 <FaFilePdf className="print-icon-page" onClick={handlePrint} title="Print Selected" />
//                               )}
//                               {Object.values(selectedRecords).flat().length > 0 && (
//                                 <FaTrash className="delete-icon-page" onClick={handleDelete} title="Delete Selected" />
//                               )}
//                             </div>

//                             <table className="sub-payroll">
//                               <thead>
//                                 <tr>
//                                   <th className="check-sel-head">
//                                     <input
//                                       className="check-input"
//                                       type="checkbox"
//                                       checked={selectAll[key] || false}
//                                       onChange={() => handleSelectAll(key, expandedPeriods[key])}
//                                     />
//                                   </th>
//                                   <th>Name</th>
//                                   <th>Gross Pay</th>
//                                   <th>Net Pay</th>
//                                   <th>Total Hours</th>
//                                 </tr>
//                               </thead>
//                               <tbody>
//                                 {expandedPeriods[key].map((record) => (
//                                   <tr key={record.id} className="sub-table-datarow">
//                                     <td>
//                                       <input
//                                         className="single-check"
//                                         type="checkbox"
//                                         checked={selectedRecords[key]?.some((r) => r.id === record.id) || false}
//                                         onChange={() =>
//                                           handleCheckboxChange(key, {
//                                             id: record.id,
//                                             employee_id: record.employee_id,
//                                             start_date: record.start_date,
//                                             end_date: record.end_date,
//                                             pay_date: record.pay_date,
//                                           })
//                                         }
//                                       />
//                                     </td>
//                                     <td>{record.name || ""}</td>
//                                     <td>{record.grosspay}</td>
//                                     <td>{record.net_pay}</td>
//                                     <td>
//                                       {(
//                                         Number(record.regularhours || 0) +
//                                         Number(record.overtimehours || 0) +
//                                         Number(record.doubletimehours || 0)
//                                       ).toFixed(2)}
//                                     </td>
//                                   </tr>
//                                 ))}
//                               </tbody>
//                             </table>
//                           </>
//                         ) : (
//                           <p>No payroll records available for this period.</p>
//                         )}
//                       </td>
//                     </tr>
//                   )}
//                 </React.Fragment>
//               );
//             })}
//           </tbody>
//         </table>
//       </div>

//       {/* ===== New Payroll panel ===== */}
// <div className={`pw-card tab-panel ${activeTab === "new" ? "is-active" : ""}`}>
//   {activeTab === "new" && (
//     <RunNewPay
//       embedded
//       onDone={() => {
//         // refresh your overview after running payroll
//         fetchPayPeriods();     // this already exists in ViewPayrollPage
//         setActiveTab("overview");
//       }}
//     />
//   )}
// </div>


//       {/* -------- All Payrolls panel -------- */}
//       <div className={`pw-card tab-panel ${activeTab === "all" ? "is-active" : ""}`}>
//         <div className="starteditbuttons">
//           <button
//             className="edit-last"
//             onClick={() => navigate(`/dashboard/business/${businessId}/list-page`)}
//           >
//             Payroll List
//           </button>
//         </div>
//       </div>
//     </div>
//   );
// }





// import React, { useCallback, useEffect, useState, useMemo } from "react";
// import { useNavigate, useParams } from "react-router-dom";
// import api from "../services/api";
// import "./ViewPayroll.css";
// import { toast } from "react-toastify";
// import { FaTrash } from "react-icons/fa";
// import { FaFilePdf } from "react-icons/fa6";
// import { ResponsiveContainer, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend } from "recharts";

// const ViewPayrollPage = () => {
//     const { id: businessId } = useParams();
    
//     const [payPeriods, setPayPeriods] = useState([]);
//     const [expandedPeriods, setExpandedPeriods] = useState({});
//     const [loading, setLoading] = useState(true);
//     const [error, setError] = useState(null);
//     const [selectedRecords, setSelectedRecords] = useState({});
//     const [selectAll, setSelectAll] = useState({});

//     // ✅ NEW: stats keyed by ISO pay_date YYYY-MM-DD
//     const [statsByPayDate, setStatsByPayDate] = useState({});

//     const [incomeByPeriod, setIncomeByPeriod] = useState({}); // key => total income


//     const navigate = useNavigate();


// const fetchPayPeriods = useCallback(async () => {
//     try {
//         const response = await api.get(`/payroll/periods/${businessId}`);
//         const data = response.data;

//         if (data.length === 0) {
//             navigate(`/dashboard/business/${businessId}/start-payroll`);
//         } else {
//             setPayPeriods(data);
//         }

//         setError(null);
//     } catch (err) {
//         console.error("Error fetching pay periods:", err);
//         setError("Failed to fetch pay periods. Please try again.");
//     } finally {
//         setLoading(false);
//     }
// }, [businessId, navigate]);

// useEffect(() => {
//     fetchPayPeriods();
// }, [fetchPayPeriods]); // ✅ Now the dependency warning is gone!

// // ✅ NEW: fetch stats (minimal, self-contained; no new deps warnings)
// useEffect(() => {
//     let mounted = true;
//     (async () => {
//         try {
//             const res = await api.get(`/payroll/stats/${businessId}`);
//             const rows = Array.isArray(res.data) ? res.data : [];
//             const map = {};
//             for (const r of rows) {
//                 // r.pay_date is expected as 'YYYY-MM-DD'
//                map[dateKey(r.pay_date)] = r;
//             }
//             if (mounted) setStatsByPayDate(map);
//         } catch (e) {
//             console.error("Error fetching payroll stats:", e);
//         }
//     })();
//     return () => { mounted = false; };
// }, [businessId]);



// useEffect(() => {
//   let mounted = true;
//   (async () => {
//     try {
//       const { data } = await api.get(`/business/${businessId}/income-totals/all-periods`);
//       const map = {};
//       for (const r of (data?.periods || [])) {
//         // Normalize the dates coming from the server; DO NOT trust r.key
//         const k = `${dateKey(r.start_date)}_${dateKey(r.end_date)}_${dateKey(r.pay_date)}`;
//         map[k] = Number(r.total_income || 0);
//       }
//       if (mounted) setIncomeByPeriod(map);
//       // console.log('incomeByPeriod keys:', Object.keys(map));
//     } catch (e) {
//       console.error("Error fetching all-period income totals:", e);
//     }
//   })();
//   return () => { mounted = false; };
// }, [businessId]);






//     const handleCheckboxChange = (periodKey, record) => {
//         setSelectedRecords((prev) => {
//             const updated = { ...prev };
    
//             // Initialize periodKey if not present
//             if (!updated[periodKey]) updated[periodKey] = [];
    
//             const existingRecord = updated[periodKey].find((r) => r.id === record.id);
    
//             if (existingRecord) {
//                 // Remove the record if it's already selected
//                 updated[periodKey] = updated[periodKey].filter((r) => r.id !== record.id);
//             } else {
//                 // Add the full record if it's not selected
//                 updated[periodKey] = [...updated[periodKey], record];
//             }
    
//             // Update the 'Select All' state based on the new selection
//             setSelectAll((prev) => ({
//                 ...prev,
//                 [periodKey]: updated[periodKey].length === expandedPeriods[periodKey].length,
//             }));
    
//             return updated;
//         });
//     };
    

//     const fmtPercent = (n, digits = 1) =>
//   typeof n === "number" && Number.isFinite(n)
//     ? `${n.toFixed(digits)}%`
//     : "-";




//     const handleSelectAll = (periodKey, records) => {
//         setSelectAll((prev) => {
//             const isSelectingAll = !prev[periodKey]; // Toggle select all
    
//             setSelectedRecords((prevRecords) => {
//                 const updatedRecords = { ...prevRecords };
//                 updatedRecords[periodKey] = isSelectingAll ? records : [];
//                 return updatedRecords;
//             });
    
//             return { ...prev, [periodKey]: isSelectingAll };
//         });
//     };
    

//     const toNum = (v) => {
//   if (v == null) return null;
//   if (typeof v === "number") return Number.isFinite(v) ? v : null;
//   const n = parseFloat(String(v).replace(/[^0-9.-]/g, "")); // "$1,234.56" -> 1234.56
//   return Number.isFinite(n) ? n : null;
// };

// const calcDomain = (data, key) => {
//   const vals = data.map(d => d[key]).filter(Number.isFinite);
//   if (!vals.length) return [0, 1];                // fallback so axis isn't all zeros
//   const min = Math.min(...vals), max = Math.max(...vals);
//   if (min === max) return min === 0 ? [0, 1] : [min * 0.95, max * 1.05];
//   return [Math.floor(min), Math.ceil(max)];
// };



// const dateKey = (d) => {
//   if (!d) return "";
//   if (typeof d === "string") return d.slice(0, 10);   // handles 'YYYY-MM-DD' and ISO
//   return new Date(d).toISOString().slice(0, 10);
// };

// const periodKeyOf = (p) =>
//   `${dateKey(p.start_date)}_${dateKey(p.end_date)}_${dateKey(p.pay_date)}`;



// // Pretty date for axes/tooltips
// const fmtShortDate = (s) => {
//   if (!s) return "";
//   const d = new Date(s);
//   return d.toLocaleDateString();
// };

// // Build a time series from statsByPayDate
// const chartData = useMemo(() => {
//   const rows = Object.entries(statsByPayDate).map(([date, r]) => ({
//     date,
//     payroll_cost: toNum(r?.payroll_cost),
//     total_hours: toNum(r?.total_hours),
//     overtime_hours: toNum(r?.overtime_hours),
//     avg_pay_rate: toNum(r?.avg_pay_rate),
//   }));
//   rows.sort((a, b) => a.date.localeCompare(b.date));
//   return rows;
// }, [statsByPayDate]);


// // Axis label formatters (keep labels short; tooltip shows full values)
// const fmtCurrencyAxis = (v) =>
//   v == null
//     ? ""
//     : new Intl.NumberFormat(undefined, {
//         style: "currency",
//         currency: "USD",
//         notation: "compact",      // $1.2K, $3.4M
//         maximumFractionDigits: 1,
//       }).format(Number(v));

// const fmtNumberAxis = (v) =>
//   v == null ? "" : Number(v).toLocaleString(undefined, { maximumFractionDigits: 2 });
   


//     const handlePrint = async () => {
//         try {
//             const allSelectedRecords = Object.values(selectedRecords).flat();
//             const response = await api.post(
//                 `/payroll/print`,
//                 { ids: allSelectedRecords.map((r) => r.id) },
//                 { responseType: "blob" }
//             );
            
//             const blob = new Blob([response.data], { type: "application/pdf" });
//             const link = document.createElement("a");
//             link.href = URL.createObjectURL(blob);
//             link.download = "payroll_records.pdf";
//             link.click();
//             setSelectedRecords({});
//             setSelectAll({});
//         } catch (err) {
//             console.error("Error printing payroll records:", err);
//             alert("Failed to print selected records. Please try again.");
//         }
//     };

   



//     const handleDelete = async () => {
//         try {
//             const allSelectedRecords = Object.values(selectedRecords).flat();
    
//             if (allSelectedRecords.length === 0) {
//                 toast.error("No records selected for deletion.");
//                 return;
//             }
    
//             // Send the full record details to the backend
//             await api.post(`/payroll/delete`, { payrollData: allSelectedRecords });
    
//             await fetchPayPeriods(); 
    
//             setExpandedPeriods((prev) => {
//                 const updatedPeriods = { ...prev };
//                 Object.keys(updatedPeriods).forEach((key) => {
//                     if (updatedPeriods[key]) {
//                         updatedPeriods[key] = updatedPeriods[key].filter(
//                             (record) => !allSelectedRecords.some((selected) => selected.id === record.id)
//                         );
//                     }
//                 });
//                 return updatedPeriods;
//             });
    
//             setSelectedRecords({});
//             setSelectAll({});
//             toast.success("Selected records deleted successfully!");
//         } catch (err) {
//             console.error("Error deleting payroll records:", err);
//             toast.error("Failed to delete selected records. Please try again.");
//         }
//     };
    





//     const toggleExpandPeriod = async (period) => {
//         const periodKey = `${period.start_date}_${period.end_date}_${period.pay_date}`;
//         if (expandedPeriods[periodKey]) {
//             setExpandedPeriods((prev) => ({ ...prev, [periodKey]: null }));
//         } else {
//             try {
//                 const response = await api.get(`/payroll/records/${businessId}`, {
//                     params: {
//                         start_date: new Date(period.start_date).toISOString().slice(0, 10),
//                         end_date: new Date(period.end_date).toISOString().slice(0, 10),
//                         pay_date: new Date(period.pay_date).toISOString().slice(0, 10),
//                     },
//                 });
//                 setExpandedPeriods((prev) => ({ ...prev, [periodKey]: response.data }));
//             } catch (err) {
//                 console.error("Error fetching payroll records:", err);
//                 alert("Failed to fetch payroll records. Please try again.");
//             }
//         }
//     };

//     if (loading) return <p>Loading payroll periods...</p>;
//     if (error) return <p>{error}</p>;

//     // ✅ NEW: simple formatters (local, no side-effects)
//     const fmtCurrency = (n) =>
//         typeof n === "number" ? n.toLocaleString(undefined, { style: "currency", currency: "USD" }) : "-";
//     const fmtNumber = (n, digits = 2) =>
//         typeof n === "number" ? n.toLocaleString(undefined, { minimumFractionDigits: digits, maximumFractionDigits: digits }) : "-";
//     const showNum = (v) => (v == null || Number(v) === 0 ? "-" : fmtNumber(v));


//     return (
//         <div className="view-payroll-page">
//             <h4 className="record-header-view">Payroll Records</h4>
            

//             {/* ===== Payroll Charts ===== */}
// {chartData.length > 0 && (
//   <div className="view-charts-grid">
//     {/* Payroll Cost */}
//     <div className="chart-card">
      
//       <ResponsiveContainer width="100%" height={240}>
//         <LineChart data={chartData} margin={{ top: 8, right: 16, bottom: 8, left: 0 }}>
//           <CartesianGrid strokeDasharray="3 3" />
//           <XAxis dataKey="date" tickFormatter={fmtShortDate} />
//           <YAxis
//             width={80}
//             tickMargin={8}
//             tickFormatter={fmtCurrencyAxis}
//             domain={calcDomain(chartData, "payroll_cost")}
//             />
//           <Tooltip
//             labelFormatter={fmtShortDate}
//             formatter={(v) => [v?.toLocaleString(undefined, { style: "currency", currency: "USD" }), "Cost"]}
//           />
//           <Legend />
//           <Line type="linear" dataKey="payroll_cost" name="Total Cost" dot={false} stroke="#ff4d4f"  />
//         </LineChart>
//       </ResponsiveContainer>
//     </div>

//     {/* Total Hours */}
//     <div className="chart-card">
      
//       <ResponsiveContainer width="100%" height={240}>
//         <LineChart data={chartData} margin={{ top: 8, right: 16, bottom: 8, left: 0 }}>
//           <CartesianGrid strokeDasharray="3 3" />
//           <XAxis dataKey="date" tickFormatter={fmtShortDate} />
//           <YAxis
//   width={60}
//   tickMargin={8}
//   tickFormatter={fmtNumberAxis}
//   domain={calcDomain(chartData, "total_hours")}
// />
//           <Tooltip
//             labelFormatter={fmtShortDate}
//             formatter={(v) => [Number(v).toLocaleString(undefined, { maximumFractionDigits: 2 }), "Hours"]}
//           />
//           <Legend />
//           <Line type="linear" dataKey="total_hours" name=" Total Hours" dot={false} stroke="#52c41a" />
//         </LineChart>
//       </ResponsiveContainer>
//     </div>

//     {/* Overtime Hours */}
//     <div className="chart-card">
      
//       <ResponsiveContainer width="100%" height={240}>
//         <LineChart data={chartData} margin={{ top: 8, right: 16, bottom: 8, left: 0 }}>
//           <CartesianGrid strokeDasharray="3 3" />
//           <XAxis dataKey="date" tickFormatter={fmtShortDate} />
//           <YAxis
//   width={60}
//   tickMargin={8}
//   tickFormatter={fmtNumberAxis}
//   domain={calcDomain(chartData, "overtime_hours")}
// />
//           <Tooltip
//             labelFormatter={fmtShortDate}
//             formatter={(v) => [Number(v).toLocaleString(undefined, { maximumFractionDigits: 2 }), "OT Hours"]}
//           />
//           <Legend />
//           <Line type="linear" dataKey="overtime_hours" name="Overtime Hours" dot={false} stroke="#1890ff" />
//         </LineChart>
//       </ResponsiveContainer>
//     </div>

//     {/* Average Pay Rate */}
//     <div className="chart-card">
      
//       <ResponsiveContainer width="100%" height={240}>
//         <LineChart data={chartData} margin={{ top: 8, right: 16, bottom: 8, left: 0 }}>
//           <CartesianGrid strokeDasharray="3 3" />
//           <XAxis dataKey="date" tickFormatter={fmtShortDate} />
//           <YAxis
//   width={80}
//   tickMargin={8}
//   tickFormatter={fmtCurrencyAxis}
//   domain={calcDomain(chartData, "avg_pay_rate")}
// />
//           <Tooltip
//             labelFormatter={fmtShortDate}
//             formatter={(v) => [v?.toLocaleString(undefined, { style: "currency", currency: "USD" }), "Avg Rate"]}
//           />
//           <Legend />
//           <Line type="linear" dataKey="avg_pay_rate" name="Avg Rate" dot={false} stroke="#faad14" />
//         </LineChart>
//       </ResponsiveContainer>
//     </div>
//   </div>
// )}
// {/* ===== End Payroll Charts ===== */}



//             <div className="starteditbuttons">
//                 <button className="start-new" onClick={() => navigate(`/dashboard/business/${businessId}/run-new-pay`)}>
//                     Start New Payroll
//                 </button>
//                 <button className="edit-last" onClick={() => navigate(`/dashboard/business/${businessId}/editlastpayroll`)}>
//                     Edit Last Payroll
//                 </button>
//                 <button className="edit-last" onClick={() => navigate(`/dashboard/business/${businessId}/list-page`)}>
//                     Payroll List
//                 </button>
//             </div>

//             {payPeriods.length > 0 ? (
//                 <table className="payroll-table-view">
//                     <thead>
//                         <tr>
//                             <th>Pay Date</th>
//                             {/* ✅ NEW HEADERS */}
//                             <th>Payroll Cost</th>
//                             <th>Total Hours</th>
//                             <th>Overtime Hours</th>
//                             <th>Avg Pay Rate</th>
                            
                            
//                         </tr>
//                     </thead>
//                     <tbody>
//                         {payPeriods.map((period) => {
//                             const periodKey = `${period.start_date}_${period.end_date}_${period.pay_date}`;
//                             // ✅ key stats lookup by normalized pay date
                            
//                             const stats = statsByPayDate[dateKey(period.pay_date)];

//                             return (
//                                 <React.Fragment key={periodKey}>
//                                     <tr onClick={() => toggleExpandPeriod(period)} className="clickable-row">
//                                         <td>{new Date(period.pay_date).toLocaleDateString()}</td>
//                                         {/* ✅ NEW CELLS */}
//                                         <td>
//                                         <div>{stats ? fmtCurrency(stats.payroll_cost) : "-"}</div>
//                                         <div className="subtext-percent">
//                                             {(() => {
//                                             const key = periodKeyOf(period);      // ✅ normalized
//                                             const income = Number(incomeByPeriod[key] || 0);
//                                             const cost = toNum(stats?.payroll_cost);
//                                             if (!Number.isFinite(cost)) return "-";
//                                             if (income <= 0) return "0.0%";       // or "-" if you prefer
//                                             const pct = (cost / income) * 100;
//                                             return fmtPercent(pct, 1);
//                                             })()}
//                                         </div>
//                                         </td>


//                                         <td>{showNum(stats?.total_hours)}</td>
//                                         <td>{showNum(stats?.overtime_hours)}</td>

//                                         <td>{stats && stats.avg_pay_rate !== null ? fmtCurrency(stats.avg_pay_rate) : "-"}</td>
                                        
                                        
//                                     </tr>
//                                     {expandedPeriods[periodKey] && (
//                                         <tr>
//                                             <td colSpan="7">
                                                
//                                                 {expandedPeriods[periodKey].length > 0 ? (
//                                                     <>
                                                    

//                                                     <div className="view-page-icons">
//                                                         {Object.values(selectedRecords).flat().length > 0 && (
//                                                             <FaFilePdf className="print-icon-page" onClick={handlePrint} title="Print Selected" />
//                                                         )}
//                                                         {Object.values(selectedRecords).flat().length > 0 && (
//                                                             <FaTrash className="delete-icon-page" onClick={handleDelete} title="Delete Selected" />
//                                                         )}
                                                
//                                                     </div>

                                                        
//                                                         <table className="sub-payroll">
//                                                             <thead>
//                                                                 <tr>
//                                                                     <th className="check-sel-head">
                                                                        
//                                                                             <input
//                                                                                 className="check-input"
//                                                                                 type="checkbox"
//                                                                                 checked={
//                                                                                     selectAll[periodKey] || false
//                                                                                 }
//                                                                                 onChange={() =>
//                                                                                     handleSelectAll(
//                                                                                         periodKey,
//                                                                                         expandedPeriods[periodKey]
//                                                                                     )
//                                                                                 }
//                                                                             />
                                                                            
                                                                       
//                                                                     </th>
//                                                                     <th>Name</th>
//                                                                     <th>Gross Pay</th>
//                                                                     <th>Net Pay</th>
//                                                                     <th>Total Hours</th>
//                                                                 </tr>
//                                                             </thead>
//                                                             <tbody>
//                                                                 {expandedPeriods[periodKey].map((record) => (
//                                                                     <tr key={record.id} className="sub-table-datarow">
                                                                        
//                                                                         <td>
//                                                                             <input
//                                                                                 className="single-check"
//                                                                                 type="checkbox"
//                                                                                 checked={selectedRecords[periodKey]?.some((r) => r.id === record.id) || false}
//                                                                                 onChange={() => handleCheckboxChange(periodKey, {
//                                                                                     id: record.id,
//                                                                                     employee_id: record.employee_id, 
//                                                                                     start_date: record.start_date,
//                                                                                     end_date: record.end_date,
//                                                                                     pay_date: record.pay_date
//                                                                                 })}
//                                                                             />
//                                                                         </td>

//                                                                         <td>{record.name || ""}</td>
//                                                                         <td>{record.grosspay}</td>
//                                                                         <td>{record.net_pay}</td>
//                                                                         <td>
//   {(Number(record.regularhours || 0)
//    + Number(record.overtimehours || 0)
//    + Number(record.doubletimehours || 0)).toFixed(2)}
// </td>

//                                                                     </tr>
//                                                                 ))}
//                                                             </tbody>
//                                                         </table>
//                                                     </>
//                                                 ) : (
//                                                     <p>No payroll records available for this period.</p>
//                                                 )}
//                                             </td>
//                                         </tr>
//                                     )}
//                                 </React.Fragment>
//                             );
//                         })}
//                     </tbody>
//                 </table>
//             ) : (
//                 <p>No pay periods found.</p>
//             )}
//         </div>
//     );
// };

// export default ViewPayrollPage;



